import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ShowConditionsButton from '@Components/Results/reactions/reaction-details/ShowConditionsButton';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ShowMore from '@Components/Results/utils/ShowMore';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ReactionDetailRow from '@Components/Results/reactions/reaction-details/ReactionDetailRow';

function ReactionDetailsTable({
  metadata,
  reactionId,
  details,
  detailsCount,
  initialDetailsNumber,
  isHitDetails,
  hasHitDetails,
  spinnerId,
  index,
  showMoreHandler,
  showLessHandler,
  showNextConditions,
  toggleAllDetails,
  databaseId,
  displayFooter,
  largestProduct,
}) {
  const tableRef = useRef();
  const isCIDatabase = ReactReactionAdditionalInfoHelper.isCIDatabase(databaseId);
  const containerClass = isHitDetails ? 'reaction-hit-details' : 'reaction-details';

  useEffect(() => {
    if (!isHitDetails && hasHitDetails && tableRef.current) {
      tableRef.current.scrollTo({ top: tableRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [isHitDetails, hasHitDetails]);

  const allowShowMore = !metadata.isViewPreview || isHitDetails;
  const rows = details.map((detail, idx) => (
    <ReactionDetailRow
      metadata={metadata}
      key={idx}
      detail={detail}
      index={index}
      reactionId={reactionId}
      isHit={isHitDetails || detail.isHit}
      spinnerId={spinnerId}
      databaseId={databaseId}
      largestProduct={largestProduct}
    />
  ));

  return (
    <div className="rx-reflow-scrollable">
      <table
        ref={tableRef}
        id={`${containerClass}-${index}`}
        className="rx-reactions-table rx-table--full-width rx-table--full-height display-table c-els-table c-els-table--middle c-els-table--vertical-dividers c-els-table--subtle-dividers">
        <thead className="c-els-table__head">
          <tr className="c-els-table__row c-els-table__row--head c-els-table__row--sticky">
            <th className="rx-reactions-table__conditions c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">
              Conditions
            </th>
            <th className="rx-reactions-table__yield c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">Yield</th>
            <th className="rx-reactions-table__references u-els-display-none@tablet c-els-table__cell c-els-table__basic-cell c-els-table__cell--header">
              {isCIDatabase ? 'Reference/Experiment' : 'Reference'}
            </th>
          </tr>
        </thead>
        <tbody className="c-els-table__body">
          {rows}
          {displayFooter && initialDetailsNumber < detailsCount && (
            <tr
              className={`c-els-table__row ${details.length > initialDetailsNumber && detailsCount !== initialDetailsNumber ? 'sticky-show-less-row' : ''}`}>
              <td className="c-els-table__cell c-els-table__basic-cell" colSpan="4">
                <div className="rx-table__caption rx-btn-group--center">
                  {allowShowMore && (
                    <ShowMore
                      labelAll={`Show all ${isHitDetails ? 'hits' : 'conditions'}`}
                      initial={initialDetailsNumber}
                      current={details.length}
                      total={detailsCount}
                      container={`#${containerClass}-${index}`}
                      scrollToLess={`#${containerClass}-${index}`}
                      moreHandler={(args) => showMoreHandler(args)}
                      lessHandler={() => showLessHandler()}
                    />
                  )}
                  {allowShowMore && showNextConditions && (
                    <ShowConditionsButton toggleAllDetails={toggleAllDetails} showNextConditions={showNextConditions} index={index} />
                  )}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

ReactionDetailsTable.defaultProps = {
  displayFooter: true,
};

ReactionDetailsTable.propTypes = {
  metadata: MetadataPropTypesShape,
  reactionId: PropTypes.string.isRequired,
  details: PropTypes.array.isRequired,
  detailsCount: PropTypes.number.isRequired,
  initialDetailsNumber: PropTypes.number,
  isHitDetails: PropTypes.bool,
  hasHitDetails: PropTypes.bool,
  spinnerId: PropTypes.string.isRequired,
  index: PropTypes.string.isRequired,
  showMoreHandler: PropTypes.func.isRequired,
  showLessHandler: PropTypes.func.isRequired,
  showNextConditions: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  toggleAllDetails: PropTypes.func,
  databaseId: PropTypes.string.isRequired,
  displayFooter: PropTypes.bool,
  largestProduct: PropTypes.string,
};

export default ReactionDetailsTable;
